import * as React from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type { W_EquipmentUtilization_FindBySubCategoryTable_VM } from 'ab-api/web/equipmentUtilization/findBySubCategoryTable/viewModel';

import { range } from '@acceligentllc/shared/utils/array';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { EquipmentCostCategory } from '@acceligentllc/shared/enums/equipmentCostCategory';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import { bemElement } from 'af-utils/bem.util';
import { downloadCSV } from 'af-utils/csv.utils';

import EquipmentUtilizationLevel, { EquipmentUtilizationLevelLabel, EquipmentUtilizationLevelByIndex, EquipmentUtilizationLevelIndex } from 'ab-enums/equipmentUtilizationLevelNew.enum';
import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { errorToast, loadingToast, successToast } from '@acceligentllc/storybook';

import Breadcrumbs from 'af-components/Breadcrumbs';
import LoadingIndicator from 'af-components/LoadingIndicator';
import type { TableRef } from 'af-components/Table';

import * as EquipmentUtilizationActions from 'af-actions/equipmentUtilization';
import * as KPIActions from 'af-actions/kpi';

import EquipmentUtilizationGroupTable from './EquipmentUtilizationGroupTable';
import EquipmentUtilizationTable from './EquipmentUtilizationTable';
import { getTable, toCSVData, toEquipmentCostCSVData } from './helper';
import InfoCard from './InfoCard';
import type { EquipmentUtilizationTableBaseVM } from './types';

interface EquipmentUtilizationTableSelectionModel {
	level: EquipmentUtilizationLevel;
	equipmentType: Nullable<string>;
	equipmentGroupId: Nullable<string>;
	equipmentCategoryId: Nullable<string>;
	equipmentCostId: Nullable<string>;
	timePeriod: Nullable<string>;
}

type OwnProps = CustomRouteComponentProps;

type Props = OwnProps & ConnectedProps<typeof connector>;

type BreadCrumbHistoryType = {
	[key in keyof typeof EquipmentCostCategory]: {
		[timePeriod: string]: {
			[equipmentGroupId: number]: {
				name: string;
				map: {
					[categoryId: number]: {
						name: string;
						map: {
							[subCategoryId: number]: {
								name: string;
							};
						};
					};
				};
			};
		};
	};
};

const BreadCrumbLevelMapper = {
	[EquipmentUtilizationLevel.TYPE]: () => 'Equipment Utilization - Type',
	[EquipmentUtilizationLevel.GROUP]: (
		{ equipmentType, timePeriod }: EquipmentUtilizationTableSelectionModel
	) => {
		return `${equipmentType} - ${timePeriod ?? 'ALL'}`;
	},
	[EquipmentUtilizationLevel.CATEGORY]: (
		{ equipmentType, timePeriod, equipmentGroupId }: EquipmentUtilizationTableSelectionModel,
		breadCrumbHistory: BreadCrumbHistoryType
	) => {
		if (timePeriod) {
			return breadCrumbHistory[equipmentType!]?.[timePeriod!]?.[equipmentGroupId!]?.name ?? '';
		}
		return Object.values(breadCrumbHistory[equipmentType!])[0]?.[equipmentGroupId!]?.name ?? '';
	},
	[EquipmentUtilizationLevel.EQUIPMENT_COST]: (
		{ equipmentType, timePeriod, equipmentGroupId, equipmentCategoryId }: EquipmentUtilizationTableSelectionModel,
		breadCrumbHistory: BreadCrumbHistoryType
	) => {
		if (timePeriod) {
			return breadCrumbHistory[equipmentType!]?.[timePeriod!]?.[equipmentGroupId!]?.[equipmentCategoryId!]?.name ?? '';
		}
		return Object.values(breadCrumbHistory[equipmentType!])[0]?.[equipmentGroupId!]?.[equipmentCategoryId!]?.name ?? '';
	},
	[EquipmentUtilizationLevel.EQUIPMENT]: (
		{ equipmentType, timePeriod, equipmentGroupId, equipmentCategoryId, equipmentCostId }: EquipmentUtilizationTableSelectionModel,
		breadCrumbHistory: BreadCrumbHistoryType
	) => {
		if (timePeriod) {
			return breadCrumbHistory[equipmentType!]?.[timePeriod!]?.[equipmentGroupId!]?.[equipmentCategoryId!]?.[equipmentCostId!]?.name ?? '';
		}
		return Object.values(breadCrumbHistory[equipmentType!])[0]?.[equipmentGroupId!]?.[equipmentCategoryId!]?.[equipmentCostId!]?.name ?? '';
	},
};

type EquipmentUtilizationSearchableRow = {
	name: Nullable<string>;
};

const _normalizeSearchText = (value: Nullable<string>) => {
	return (value ?? '').trim().toLowerCase();
};

const _filterByFirstColumn = <T extends EquipmentUtilizationSearchableRow>(
	data: Nullable<T[]>,
	tableRequestModel: TableQuery
): Nullable<T[]> => {
	const filterByText = _normalizeSearchText(tableRequestModel.filterByText ?? null);

	if (!filterByText) {
		return data;
	}

	return data?.filter((_row) => {
		return _normalizeSearchText(_row.name).includes(filterByText);
	}) ?? null;
};

const _clearSearchFromTableQuery = (tableRequestModel: TableQuery) => {
	return new TableQuery({
		...tableRequestModel,
		filterByText: '',
	});
};

const _getActiveSearchText = (
	selection: EquipmentUtilizationTableSelectionModel,
	groupTableRef: React.RefObject<Nullable<TableRef<EquipmentUtilizationTableBaseVM>>>,
	basicTableRef: React.RefObject<Nullable<TableRef<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0]>>>
) => {
	if (selection.level === EquipmentUtilizationLevel.EQUIPMENT) {
		return basicTableRef.current?.getFilterText() ?? '';
	}

	return groupTableRef.current?.getFilterText() ?? '';
};

const _getTableQueryForExport = (filterByText: string) => {
	return new TableQuery({
		page: 0,
		pageSize: 0,
		filterByText,
	});
};

const _getEquipmentUtilizationTable = (
	data: Nullable<EquipmentUtilizationTableBaseVM[]>,
	selection: EquipmentUtilizationLevel,
	tableRequestModel: TableQuery
) => {
	const filteredData = _filterByFirstColumn(data, tableRequestModel);
	const sanitizedRequestModel = _clearSearchFromTableQuery(tableRequestModel);

	return getTable(
		sanitizedRequestModel,
		selection,
		filteredData
	) ?? {} as TableContent<EquipmentUtilizationTableBaseVM>;
};

const _getEquipmentUtilizationTableForEquipment = (
	data: Nullable<W_EquipmentUtilization_FindBySubCategoryTable_VM['items']>,
	selection: EquipmentUtilizationLevel,
	tableRequestModel: TableQuery
) => {
	const filteredData = _filterByFirstColumn(data, tableRequestModel);
	const sanitizedRequestModel = _clearSearchFromTableQuery(tableRequestModel);

	return (getTable(
		sanitizedRequestModel,
		selection,
		filteredData
	) ?? {}) as TableContent<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0]>;
};

const EquipmentUtilization: React.FC<Props> = (props) => {
	const {
		location: { state: { orgAlias } },
		companyName,
		history,
		fetchEquipmentUtilizationForCompanyTable,
		fetchEquipmentUtilizationByTypeTable,
		fetchEquipmentUtilizationByGroupTable,
		fetchEquipmentUtilizationByCategoryTable,
		fetchEquipmentUtilizationBySubCategoryTable,
		refreshEquipmentUtilizationCache,
	} = props;

	const groupTableRef = React.useRef<TableRef<EquipmentUtilizationTableBaseVM>>(null);

	const basicTableRef = React.useRef<TableRef<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0]>>(null);

	const [ignoredTimePeriodId, setIgnoredTimePeriodId] = React.useState<Nullable<string>>(null);
	const [initialData, setInitialData] = React.useState<Nullable<EquipmentUtilizationTableBaseVM[]>>(null);
	const [currentData, setCurrentData] = React.useState<Nullable<EquipmentUtilizationTableBaseVM[]>>(null);
	const [equipmentCost, setEquipmentCost] = React.useState<Nullable<W_EquipmentUtilization_FindBySubCategoryTable_VM['items']>>(null);
	const [loading, setLoading] = React.useState(false);
	const [breadCrumbHistory, setBreadCrumbHistory] = React.useState<BreadCrumbHistoryType>({
		[EquipmentCostCategory.EQUIPMENT]: {

		},
		[EquipmentCostCategory.ACCESSORY]: {

		},
		[EquipmentCostCategory.SMALL_TOOL]: {

		},
	});

	const [selection, setSelection] = React.useState<EquipmentUtilizationTableSelectionModel>({
		level: EquipmentUtilizationLevel.TYPE,
	} as EquipmentUtilizationTableSelectionModel);

	const loadData = React.useCallback(async () => {
		const _data = await fetchEquipmentUtilizationForCompanyTable();
		setInitialData(_data.items);
		setLoading(false);
	}, [fetchEquipmentUtilizationForCompanyTable]);

	// On mount
	React.useEffect(() => {
		setLoading(true);
		loadData();
	}, [loadData]);

	const fetchTable = React.useCallback(async (tableRequestModel: TableQuery) => {
		if (!selection) {
			return;
		}
		const {
			level,
			timePeriod,
			equipmentType,
			equipmentGroupId,
			equipmentCategoryId,
		} = selection;

		let _data: Nullable<EquipmentUtilizationTableBaseVM[]>;
		switch (level) {
			case EquipmentUtilizationLevel.TYPE:
				_data = initialData;
				break;
			case EquipmentUtilizationLevel.GROUP:
				_data = (await fetchEquipmentUtilizationByTypeTable(EquipmentCostCategory[equipmentType!], timePeriod!)).items;
				break;
			case EquipmentUtilizationLevel.CATEGORY:
				_data = (await fetchEquipmentUtilizationByGroupTable(+equipmentGroupId!, timePeriod!)).items;
				break;
			case EquipmentUtilizationLevel.EQUIPMENT_COST:
				_data = (await fetchEquipmentUtilizationByCategoryTable(
					+equipmentCategoryId!,
					timePeriod!
				)).items;
				break;
			default:
				throw new Error('Unknown utilization type');
		}
		setCurrentData(_data);
		return _getEquipmentUtilizationTable(_data, selection.level, tableRequestModel);
	}, [
		selection,
		initialData,
		fetchEquipmentUtilizationByTypeTable,
		fetchEquipmentUtilizationByGroupTable,
		fetchEquipmentUtilizationByCategoryTable,
	]);

	React.useEffect(() => {
		groupTableRef.current?.refreshTable();
	}, [fetchTable]);

	React.useEffect(() => {
		if (!selection || !currentData || selection.level === EquipmentUtilizationLevel.EQUIPMENT) {
			return;
		}
		const {
			level,
			equipmentType,
			equipmentGroupId,
			equipmentCategoryId,
		} = selection;

		const newBreads = { ...breadCrumbHistory };
		switch (level) {
			case EquipmentUtilizationLevel.GROUP:
				for (const _row of currentData ?? []) {
					newBreads[equipmentType!] = {
						...newBreads[equipmentType!],
						[_row.timePeriod!]: {
							...newBreads[equipmentType!][_row.timePeriod!],
							[_row.groupId]: {
								name: _row.name,
							},
						},
					};
				}
				break;
			case EquipmentUtilizationLevel.CATEGORY:
				for (const _row of currentData ?? []) {
					newBreads[equipmentType!] = {
						...newBreads[equipmentType!],
						[_row.timePeriod!]: {
							...newBreads[equipmentType!][_row.timePeriod!],
							[equipmentGroupId!]: {
								...newBreads[equipmentType!][_row.timePeriod!][equipmentGroupId!],
								[_row.groupId]: {
									name: _row.name,
								},
							},
						},
					};
				}
				break;
			case EquipmentUtilizationLevel.EQUIPMENT_COST:
				for (const _row of currentData ?? []) {
					newBreads[equipmentType!] = {
						...newBreads[equipmentType!],
						[_row.timePeriod!]: {
							...newBreads[equipmentType!][_row.timePeriod!],
							[equipmentGroupId!]: {
								...(newBreads[equipmentType!][_row.timePeriod!]?.[equipmentGroupId!] ?? {}),
								[equipmentCategoryId!]: {
									...(newBreads[equipmentType!][_row.timePeriod!]?.[equipmentGroupId!]?.[equipmentCategoryId!] ?? {}),
									[_row.groupId]: {
										name: _row.name,
									},
								},
							},
						},
					};
				}
				break;
			default:
				return;
		}
		setBreadCrumbHistory(newBreads);
		// Only when selection changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentData]);

	React.useEffect(() => {
		if (!selection || !equipmentCost || selection.level !== EquipmentUtilizationLevel.EQUIPMENT) {
			return;
		}
		const {
			timePeriod,
			equipmentType,
			equipmentGroupId,
			equipmentCategoryId,
			equipmentCostId,
		} = selection;

		const newBreads = { ...breadCrumbHistory };
		for (const _equipment of equipmentCost ?? []) {
			newBreads[equipmentType!] = {
				...newBreads[equipmentType!],
				[timePeriod!]: {
					...newBreads[equipmentType!][timePeriod!],
					[equipmentGroupId!]: {
						...newBreads[equipmentType!][timePeriod!][equipmentGroupId!],
						[equipmentCategoryId!]: {
							...newBreads[equipmentType!][timePeriod!][equipmentGroupId!][equipmentCategoryId!],
							[equipmentCostId!]: {
								...newBreads[equipmentType!][timePeriod!][equipmentGroupId!][equipmentCategoryId!][equipmentCostId!],
								[_equipment.groupId]: {
									name: _equipment.name,
								},
							},
						},
					},
				},
			};
		}
		setBreadCrumbHistory(newBreads);
		// Only when selection changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selection, equipmentCost]);

	const fetchEquipmentTable = React.useCallback(async (tableRequestModel: TableQuery) => {
		if (!selection) {
			return;
		}
		const { timePeriod, equipmentCostId } = selection;

		const result = await fetchEquipmentUtilizationBySubCategoryTable(
			+equipmentCostId!,
			timePeriod!
		);
		setEquipmentCost(result.items);
		return _getEquipmentUtilizationTableForEquipment(result.items, selection.level, tableRequestModel);
	}, [fetchEquipmentUtilizationBySubCategoryTable, selection]);

	React.useEffect(() => {
		basicTableRef?.current?.refreshTable();
	}, [fetchEquipmentTable]);

	const goDownInSelection = React.useCallback(async (groupingId: string, timePeriodId: string) => {
		switch (selection.level) {
			case EquipmentUtilizationLevel.TYPE:
				setSelection({
					...selection,
					level: EquipmentUtilizationLevel.GROUP,
					equipmentType: groupingId,
					timePeriod: timePeriodId,
				});
				break;
			case EquipmentUtilizationLevel.GROUP:
				setSelection({
					...selection,
					level: EquipmentUtilizationLevel.CATEGORY,
					equipmentGroupId: groupingId,
					timePeriod: timePeriodId,
				});
				break;
			case EquipmentUtilizationLevel.CATEGORY:
				setSelection({
					...selection,
					level: EquipmentUtilizationLevel.EQUIPMENT_COST,
					equipmentCategoryId: groupingId,
					timePeriod: timePeriodId,
				});
				break;
			case EquipmentUtilizationLevel.EQUIPMENT_COST:
				setSelection({
					...selection,
					level: EquipmentUtilizationLevel.EQUIPMENT,
					equipmentCostId: groupingId,
					timePeriod: timePeriodId,
				});
				break;
			default:
				console.error('Invalid Equipment Utilization Level');
		}
		setIgnoredTimePeriodId(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selection.level]);

	const goToSection = React.useCallback((level: EquipmentUtilizationLevel) => {
		switch (level) {
			case EquipmentUtilizationLevel.TYPE:
				setIgnoredTimePeriodId(null);
				setSelection({
					...selection,
					level: EquipmentUtilizationLevel.TYPE,
					equipmentType: null,
					equipmentGroupId: null,
					equipmentCategoryId: null,
					equipmentCostId: null,
					timePeriod: null,
				});
				break;
			case EquipmentUtilizationLevel.GROUP:
				setSelection({
					...selection,
					level: EquipmentUtilizationLevel.GROUP,
					equipmentGroupId: null,
					equipmentCategoryId: null,
					equipmentCostId: null,
				});
				break;
			case EquipmentUtilizationLevel.CATEGORY:
				setSelection({
					...selection,
					level: EquipmentUtilizationLevel.CATEGORY,
					equipmentCategoryId: null,
					equipmentCostId: null,
				});
				break;
			case EquipmentUtilizationLevel.EQUIPMENT_COST:
				setSelection({
					...selection,
					level: EquipmentUtilizationLevel.EQUIPMENT_COST,
					equipmentCostId: null,
				});
				break;
			default:
				console.error('Invalid Equipment Utilization Level');
		}
	}, [selection]);

	const onDownloadCSVClick = React.useCallback(async () => {
		let csvData: Nullable<string>[][];
		const toastId = `eq-utilization-${selection.level}`;
		const activeSearchText = _getActiveSearchText(selection, groupTableRef, basicTableRef);
		const exportTableQuery = _getTableQueryForExport(activeSearchText);

		loadingToast(
			toastId,
			{ title: 'Exporting equipment utilization...', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
		);

		if (selection.level === EquipmentUtilizationLevel.EQUIPMENT) {
			if (!equipmentCost) {
				errorToast(
					toastId,
					{ title: 'Utilization data not available', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
				);
				throw new Error('Data not available');
			}

			const filteredEquipmentCost = _filterByFirstColumn(equipmentCost, exportTableQuery);

			csvData = toEquipmentCostCSVData(filteredEquipmentCost ?? []);

		} else {
			if (!currentData) {
				errorToast(
					toastId,
					{ title: 'Utilization data not available', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
				);
				throw new Error('Data not available');
			}

			const filteredCurrentData = _filterByFirstColumn(currentData, exportTableQuery);

			csvData = toCSVData(
				filteredCurrentData ?? [],
				EquipmentUtilizationLevelLabel[selection.level]
			);
		}

		const filename = `${companyName}_equipment-utilization_${selection.level}.csv`;
		downloadCSV(csvData, filename);

		successToast(
			toastId,
			{ title: 'Eq. utilization export complete', description: filename, timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
		);
	}, [companyName, currentData, equipmentCost, selection]);

	const changeEquipmentCost = React.useCallback((equipmentCostId: string) => {
		setSelection({ ...selection, equipmentCostId });
	}, [selection]);

	const toggleTimePeriodIgnore = React.useCallback(() => {
		const oldTimePeriodId = selection.timePeriod;
		const newTimePeriodId = ignoredTimePeriodId;

		setIgnoredTimePeriodId(oldTimePeriodId);
		setSelection({
			...selection,
			timePeriod: newTimePeriodId,
		});
	}, [ignoredTimePeriodId, selection]);

	const renderToggleTimePeriodIgnore = React.useCallback(() => {
		const selectedTimePeriod = selection.timePeriod ?? ignoredTimePeriodId;

		return (
			<ButtonGroup className="utilization-view__year-switch">
				<Button
					active={selection.timePeriod !== null}
					className={bemElement('btn', 'switch-group-item')}
					onClick={toggleTimePeriodIgnore}
				>
					Year {selectedTimePeriod}
				</Button>
				<Button
					active={ignoredTimePeriodId !== null}
					className={bemElement('btn', 'switch-group-item')}
					onClick={toggleTimePeriodIgnore}
				>
					All Years
				</Button>
			</ButtonGroup>
		);
	}, [ignoredTimePeriodId, selection.timePeriod, toggleTimePeriodIgnore]);

	const buttons = React.useMemo(() => {
		return [{
			type: TableButtonType.EXPORT,
			hasPermission: true,
			label: 'Export',
			onClick: onDownloadCSVClick,
		}];
	}, [onDownloadCSVClick]);

	const breadcrumbs = React.useMemo(() => {
		const index = EquipmentUtilizationLevelIndex[selection.level];
		return range(0, index + 1).map((_index) => {
			return {
				label: BreadCrumbLevelMapper[EquipmentUtilizationLevelByIndex[_index]](selection, breadCrumbHistory),
				onClick: goToSection.bind(this, EquipmentUtilizationLevelByIndex[_index]),
			};
		});
	}, [breadCrumbHistory, goToSection, selection]);

	const tableName = React.useMemo(() => {
		return `${TableNameEnum.EQUIPMENT_UTILIZATION}#${selection.level}`;
	}, [selection.level]);

	if (!selection.level || !EquipmentUtilizationLevel[selection.level]) {
		return null;
	}

	if (loading) {
		return (
			<div className="utilization-view form-segment">
				<Breadcrumbs items={breadcrumbs} />
				<LoadingIndicator color="orange" size="big" />
			</div>
		);
	}

	if (selection.level === EquipmentUtilizationLevel.EQUIPMENT && !!initialData) {
		const { equipmentCostId } = selection;

		const selectedEquipmentCost = currentData?.find((_equipmentCost) => _equipmentCost.groupId.toString() === equipmentCostId);

		return (
			<div className="utilization-view form-segment">
				<Breadcrumbs items={breadcrumbs} />
				<InfoCard
					changeEquipmentCost={changeEquipmentCost}
					equipmentCostOptions={currentData}
					selectedEquipmentCost={selectedEquipmentCost}
				/>
				<EquipmentUtilizationTable
					buttons={buttons}
					companyName={companyName}
					fetch={fetchEquipmentTable}
					hasSearchInput={true}
					orgAlias={orgAlias}
					redirectTo={history.push}
					searchLabel={EquipmentUtilizationLevelLabel[selection.level]}
					tableName={tableName}
					tableRef={basicTableRef}
				/>
			</div>
		);
	}

	return (
		<div className="utilization-group-view form-segment">
			<Breadcrumbs items={breadcrumbs} />
			{process.env.ENVIRONMENT === 'TESTING' && <Button onClick={refreshEquipmentUtilizationCache}>RefreshCache</Button>}
			<EquipmentUtilizationGroupTable
				additionalFilter={selection.level !== EquipmentUtilizationLevel.TYPE ? renderToggleTimePeriodIgnore : undefined}
				buttons={buttons}
				fetch={fetchTable}
				goDownInSelection={goDownInSelection}
				hasSearchInput={true}
				label={EquipmentUtilizationLevelLabel[selection.level]}
				tableName={tableName}
				tableRef={groupTableRef}
			/>
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		fetchEquipmentUtilizationForCompanyTable: EquipmentUtilizationActions.fetchEquipmentUtilizationForCompanyTable,
		fetchEquipmentUtilizationByTypeTable: EquipmentUtilizationActions.fetchEquipmentUtilizationByTypeTable,
		fetchEquipmentUtilizationByGroupTable: EquipmentUtilizationActions.fetchEquipmentUtilizationByGroupTable,
		fetchEquipmentUtilizationByCategoryTable: EquipmentUtilizationActions.fetchEquipmentUtilizationByCategoryTable,
		fetchEquipmentUtilizationBySubCategoryTable: EquipmentUtilizationActions.fetchEquipmentUtilizationBySubCategoryTable,
		refreshEquipmentUtilizationCache: KPIActions.refreshEquipmentUtilizationCache,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(EquipmentUtilization);
